import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, AlertTriangle, Clock, Heart, ArrowRight, Download,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * "Your access" status card — the single, plain-English surface telling a
 * caregiver what tier they're on and what (if anything) they need to do.
 *
 * Reads from /billing/access (cheap, computed server-side per blueprint §15
 * no-sudden-lockout rules) and renders one of:
 *   • early access ("free" state key) — launch window, everything unlocked
 *   • active     — paid, current
 *   • trialing   — Stripe trial, full access
 *   • sponsored  — locked 12-month pass, days_left countdown
 *   • grace      — payment issue but full access for N more days
 *   • past_due   — payment_failed (no grace bound yet)
 *   • expired    — read + export only, prominent renew CTA
 *
 * The bg color and CTA shift accordingly, but the messaging stays calm —
 * we never use scary red banners on a kid's communication app.
 */
const PALETTE = {
  free: { bg: "var(--gold)",  Icon: Heart },
  active: { bg: "var(--gold)",  Icon: ShieldCheck },
  trialing: { bg: "var(--gold)",  Icon: ShieldCheck },
  sponsored: { bg: "var(--gold)",  Icon: Heart },
  grace: { bg: "#FFE3E0",  Icon: Clock },
  past_due: { bg: "#FFE3E0",  Icon: AlertTriangle },
  expired: { bg: "var(--paper-muted)",  Icon: AlertTriangle },
};

const STATUS_LABELS = {
  free: "Early access",
  active: "Subscription active",
  trialing: "Trial active",
  sponsored: "Sponsored pass active",
  grace: "Grace period",
  past_due: "Payment needs attention",
  expired: "Subscription ended",
};

export default function AccessStatusCard() {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/access");
        if (alive) setAccess(data);
      } catch (err) {
        console.warn("AccessStatusCard: /billing/access failed", err?.response?.status || err?.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="brutal-card bg-white p-4 mb-4" data-testid="access-status-loading">
        <div className="display text-xs uppercase tracking-[0.18em] text-[color:var(--ink-soft)] font-bold">Your access</div>
        <div className="display font-bold mt-1">Checking…</div>
      </div>
    );
  }
  if (!access) return null;

  const palette = PALETTE[access.status] || PALETTE.active;
  const Icon = palette.Icon;
  const isExpired = access.status === "expired";
  const isUrgent = access.status === "past_due" || access.status === "grace" || isExpired;

  return (
    <div
      className="brutal-card p-5 mb-4"
      style={{ background: palette.bg }}
      data-testid={`access-status-${access.status}`}
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-10 h-10 rounded-2xl border-[3px] border-[color:var(--ink)] bg-white flex items-center justify-center">
          <Icon size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="display text-xs uppercase tracking-[0.18em] text-[color:var(--ink-soft)] font-bold">
            Your access
          </div>
          <div className="display text-xl font-extrabold leading-tight" data-testid="access-status-label">
            {STATUS_LABELS[access.status] || access.status}
          </div>
          {access.banner && (
            <p className="text-sm leading-relaxed mt-1" data-testid="access-status-banner">
              {access.banner}
            </p>
          )}
          {access.days_left !== null && access.days_left !== undefined && (
            <div className="text-xs text-[color:var(--ink-soft)] mt-1 flex items-center gap-1">
              <Clock size={11} />
              {access.days_left} day{access.days_left === 1 ? "" : "s"} remaining
              {access.status === "sponsored" && " on your sponsored pass"}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {/* Always present — never lock data away.
                Note: lapse behavior (what happens if a paid plan ends or
                payment fails) is intentionally NOT advertised here. It is
                handled with in-product messaging when it actually occurs
                — per the locked lapse-messaging spec. Removed the italic
                policy sentence 2026-07-05. */}
            <Link
              to="/privacy/me"
              className="pill-btn"
              data-testid="access-status-export-link"
            >
              <Download size={13} /> Export your boards
            </Link>
            {isUrgent && (
              <Link
                to="/pricing"
                className="pill-btn primary"
                data-testid="access-status-renew"
              >
                {isExpired ? "Choose a plan" : "Update billing"} <ArrowRight size={13} />
              </Link>
            )}
            {!isUrgent && access.status !== "free" && (
              <Link
                to="/pricing"
                className="pill-btn"
                data-testid="access-status-manage"
              >
                Manage plan <ArrowRight size={13} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
